/**
 * Admin Layout Component
 *
 * Main layout for admin pages with sidebar, topbar, and navigation
 * Follows the same design pattern as AppLayout
 */

import { useState, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Package,
  Activity,
  LogOut,
  X,
  Network,
  GitMerge,
  Bot,
  UserCog,
  BookOpen,
  Radar,
} from 'lucide-react';
import { useAuth } from '@/services/auth/AuthProvider';
import { useProfile } from '@/features/profile/hooks/useProfile';
import { MobileTaskbar } from '@/shared/components/MobileTaskbar';
import styles from './AdminLayout.module.css';

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface AdminLayoutContextType {
  toggleSidebar: () => void;
}

const AdminLayoutContext = createContext<AdminLayoutContextType | null>(null);

export const useAdminLayout = () => {
  const context = useContext(AdminLayoutContext);
  if (!context) {
    throw new Error('useAdminLayout must be used within AdminLayout');
  }
  return context;
};

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { logout, user } = useAuth();
  const { profile } = useProfile();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to sign out', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Admin navigation items
  const adminNavItems = [
    { href: '/admin', icon: LayoutDashboard, label: t('sidebar.items.dashboard'), exact: true },
    { href: '/admin/sp-applications', icon: UserCog, label: t('sidebar.items.serviceProviders') },
    { href: '/admin/directory', icon: Users, label: t('sidebar.items.directory') },
    { href: '/admin/vendors', icon: Package, label: t('sidebar.items.vendors') },
    { href: '/admin/hub-documents', icon: BookOpen, label: t('sidebar.items.hubDocuments') },
    { href: '/admin/simulator', icon: Activity, label: t('sidebar.items.simulator') },
  ];

  const integrationNavItems = [
    { href: '/admin/integrations/conflicts', icon: GitMerge, label: t('sidebar.items.conflicts') },
    { href: '/admin/integrations/monitoring', icon: Network, label: t('sidebar.items.monitoring') },
  ];

  const toolsNavItems = [
    { href: '/admin/crawler', icon: Bot, label: t('sidebar.items.crawlerControl') },
    { href: '/admin/discovery', icon: Radar, label: 'Discovery' },
    { href: '/admin/network-devices', icon: Network, label: 'Network Devices' },
  ];

  const getInitials = (name: string | null | undefined): string => {
    if (!name) return 'A';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const closeSidebar = () => setIsSidebarOpen(false);
  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);

  return (
    <AdminLayoutContext.Provider value={{ toggleSidebar }}>
      <div className={styles.layout}>
      {/* Sidebar */}
      <aside
        className={`${styles.sidebar} ${isSidebarOpen ? styles.sidebarOpen : ''}`}
      >
        {/* Mobile close button */}
        <button
          className={styles.closeButton}
          onClick={closeSidebar}
          aria-label={t('common.closeSidebar')}
        >
          <X size={24} />
        </button>

        {/* Profile Section */}
        <div className={styles.profileSection}>
          <div className={styles.profileAvatar}>
            {profile?.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt={profile.full_name || t('common.admin')}
                className={styles.profileAvatarImage}
              />
            ) : (
              getInitials(profile?.full_name || user?.email)
            )}
          </div>
          <div className={styles.profileInfo}>
            <h2 className={styles.profileName}>
              {profile?.full_name || user?.email?.split('@')[0] || t('common.admin')}
            </h2>
            <div className={styles.profileButtons}>
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className={styles.profileSignOutButton}
              >
                <LogOut size={12} />
                <span>{isLoggingOut ? t('profile.signingOut') : t('profile.signOut')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className={styles.navigation}>
          {/* Main Admin Section */}
          <div className={styles.navSection}>
            <h3 className={styles.navSectionTitle}>{t('sidebar.groups.main')}</h3>
            <ul className={styles.navList}>
              {adminNavItems.map((item) => (
                <li key={item.href}>
                  <NavLink
                    to={item.href}
                    end={item.exact}
                    className={({ isActive }) =>
                      `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
                    }
                    onClick={closeSidebar}
                  >
                    <item.icon size={20} />
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Integrations Section */}
          <div className={styles.navSection}>
            <h3 className={styles.navSectionTitle}>{t('sidebar.groups.integrations')}</h3>
            <ul className={styles.navList}>
              {integrationNavItems.map((item) => (
                <li key={item.href}>
                  <NavLink
                    to={item.href}
                    className={({ isActive }) =>
                      `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
                    }
                    onClick={closeSidebar}
                  >
                    <item.icon size={20} />
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Tools Section */}
          <div className={styles.navSection}>
            <h3 className={styles.navSectionTitle}>{t('sidebar.groups.tools')}</h3>
            <ul className={styles.navList}>
              {toolsNavItems.map((item) => (
                <li key={item.href}>
                  <NavLink
                    to={item.href}
                    className={({ isActive }) =>
                      `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
                    }
                    onClick={closeSidebar}
                  >
                    <item.icon size={20} />
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <main className={styles.mainContent}>
        {/* Page Content */}
        <div className={styles.pageContent}>{children}</div>
      </main>

      {/* Mobile Taskbar */}
      <MobileTaskbar />

        {/* Sidebar Overlay for Mobile */}
        {isSidebarOpen && (
          <div className={styles.overlay} onClick={closeSidebar} />
        )}
      </div>
    </AdminLayoutContext.Provider>
  );
};
